import { config } from '@/config';
import { reportNetworkFailure } from './crash-capture';

export interface ApiFieldError {
  field: string;
  message: string;
}

/**
 * Carries the backend's own error payload. Nothing in the UI composes an
 * error sentence - `message` is already translated server-side.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: ApiFieldError[],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors keyed by field name, for form binding. */
  get fieldMap(): Record<string, string> {
    return Object.fromEntries((this.fields ?? []).map((f) => [f.field, f.message]));
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Guarded: this module is also exercised outside a browser, and reading
    // `document` unconditionally made a request throw before it was sent.
    'x-locale': (typeof document !== 'undefined' && document.documentElement.lang)
      || config.defaultLocale,
    ...(init?.headers as Record<string, string> | undefined),
  };
  // An empty Content-Type is `upload`'s signal: drop the header entirely so
  // the browser sets multipart/form-data with its own boundary.
  if (headers['Content-Type'] === '') delete headers['Content-Type'];

  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers,
    });
  } catch (error) {
    // It never reached the server, so no request log will show it.
    reportNetworkFailure(init?.method ?? 'GET', path, error);
    throw error;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      res.status,
      body.code ?? 'errors.internal',
      body.message ?? res.statusText,
      body.fields,
      body.requestId,
    );
  }
  // A handler returning null sends 200 with no body, and `res.json()` on an
  // empty body throws - which surfaced as "could not reach the server" on a
  // request that had in fact succeeded. Absent content is `null`, not a
  // failure.
  if (res.status === 204) return undefined as T;
  const body = await res.text();
  return (body ? JSON.parse(body) : null) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * Multipart upload. `Content-Type` is deliberately omitted - the browser
   * has to set it itself so the multipart boundary is included.
   */
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(path, { method: 'POST', body: form, headers: { 'Content-Type': '' } });
  },
};
